import { useState, useEffect } from 'react';

export interface PaginationFilter<T> {
  filter: T;
  setFilter: React.Dispatch<React.SetStateAction<T>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  perPage: number;
  setPerPage: (perPage: number) => void;
  totalCount?: number;
}

export type PaginationResponse<T> = {
  data: T[];
  count: number;
};

export type BaseFilter = {
  limit: number;
  offset: number;
};

export type PaginationRequest<T> = Partial<Record<keyof T, any[]>>;

export function usePaginationFilter<T extends BaseFilter>(
  initialFilter: T,
  totalCount?: number,
): PaginationFilter<T> {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(initialFilter.limit);
  const [filter, setFilter] = useState<T>({
    ...initialFilter,
    limit: perPage,
    offset: (page - 1) * perPage,
  });

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      limit: perPage,
      offset: (page - 1) * perPage,
    }));
  }, [page, perPage]);

  useEffect(() => {
    if (totalCount !== undefined && page > Math.ceil(totalCount / perPage)) {
      setPage(1);
    }
  }, [totalCount, perPage]);

  return { filter, setFilter, page, setPage, perPage, setPerPage, totalCount };
}
